import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import moment from 'moment';
import { PageContext } from 'pages/StudentSubmissions/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Paper, ProgressBar, Stack, styled, Text, ViewMore } from 'shell/components';

const STATES = {
    // current submission slot
    READY: 1, // ready for upload
    BATCH: 2, // user has uploaded their submission, batch exists and is in progress
    SUBMISSION: 3, // submission is evaluated
    // future submission slots
    DISABLED: 4,
};

const getState = ({ batch, studentSubmissions, index }) => {
    const currentStudentSubmission = studentSubmissions[index];
    if (currentStudentSubmission) return STATES.SUBMISSION;
    if (studentSubmissions.length === index) return batch.length ? STATES.BATCH : STATES.READY;
    return STATES.DISABLED;
};

const getDraftNumberText = (index) => {
    return String(index + 1);
};

const getUploadDateText = (submission) => {
    const timestamp = submission?.created_on;
    if (!timestamp) return '--';
    return moment(timestamp).format('MMMM D, YYYY h:mm A');
};

const getPerformanceText = (submission) => {
    return submission?.ai?.ai_performance_summary ?? '--';
};

const DraftCard = ({ index }) => {
    const { batch, studentSubmissions } = useApp();
    const { t } = useTranslation('assignment');
    const { device } = useViewport();

    const { onClickViewFormative, onClickEditFormative, onClickUpload, onClickCancel, onClickViewSubmission } =
        useContext(PageContext);

    const currentStudentSubmission = studentSubmissions[index];
    const currentState = getState({ batch, studentSubmissions, index });

    const numSubmissionsInBatchCompleted = 0;
    const numSubmissionsInBatchTotal = 1;
    const canCancelBatch = false;

    return (
        <Stack sx={{ width: device !== 'desktop' ? '100%' : '260px' }} direction={'row'} spacing={5}>
            <Stack sx={{ width: '100%' }} spacing={5}>
                <CustomPaper sx={{ alignSelf: 'center', width: 'fit-content' }}>
                    <Text weight={'medium'}>Draft {getDraftNumberText(index)}</Text>
                </CustomPaper>
                <CustomPaper>
                    <Stack spacing={5}>
                        <Stack spacing={3}>
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Text color={'light'}>Draft {getDraftNumberText(index)}</Text>
                                <Text color={'light'}>{getUploadDateText(currentStudentSubmission)}</Text>
                            </Stack>
                            <Stack
                                direction={currentState === STATES.SUBMISSION ? 'column' : 'row'}
                                justifyContent={'space-between'}
                                spacing={2}
                            >
                                <Text color={currentState === STATES.SUBMISSION ? 'primary' : 'light'}>
                                    Overall Performance
                                </Text>
                                <ViewMore TextComponentProps={{ color: 'light' }} variant={'primary'}>
                                    {getPerformanceText(currentStudentSubmission)}
                                </ViewMore>
                            </Stack>
                        </Stack>
                        <Button
                            sx={{ alignSelf: 'flex-start' }}
                            disabled={currentState === STATES.BATCH || currentState === STATES.DISABLED}
                            size={'small'}
                            variant={'borderless'}
                            onClick={
                                currentState === STATES.BATCH || currentState === STATES.SUBMISSION
                                    ? () => onClickViewFormative(currentStudentSubmission.id)
                                    : onClickEditFormative
                            }
                        >
                            {currentState === STATES.BATCH || currentState === STATES.SUBMISSION
                                ? 'View Parameters'
                                : 'Edit Parameters'}
                        </Button>
                        {currentState === STATES.BATCH && (
                            <Stack>
                                <Text>Processing submission...</Text>
                                <Stack alignItems={'center'} direction={'row'} spacing={4}>
                                    <ProgressBar
                                        aria-label={'submission processing in progress'}
                                        simulateProgress={{ duration: 30, maxValue: 90 }}
                                        value={Number(
                                            (
                                                (numSubmissionsInBatchCompleted / numSubmissionsInBatchTotal) *
                                                100
                                            ).toFixed(0)
                                        )}
                                    />
                                    <IconButton
                                        aria-label={'cancel fine-tuning'}
                                        disabled={!canCancelBatch}
                                        kind={'exit'}
                                        tooltip={
                                            canCancelBatch
                                                ? { component: t('common:controls.cancel', 'Cancel') }
                                                : undefined
                                        }
                                        size={'small'}
                                        variant={'square'}
                                        onClick={onClickCancel}
                                    />
                                </Stack>
                            </Stack>
                        )}
                        {currentState !== STATES.BATCH && (
                            <>
                                {currentState === STATES.SUBMISSION ? (
                                    <Button
                                        variant={'secondary'}
                                        onClick={() => onClickViewSubmission(currentStudentSubmission.id)}
                                    >
                                        View Feedback
                                    </Button>
                                ) : (
                                    <Button disabled={currentState === STATES.DISABLED} onClick={onClickUpload}>
                                        Upload Draft
                                    </Button>
                                )}
                            </>
                        )}
                    </Stack>
                </CustomPaper>
            </Stack>
        </Stack>
    );
};

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: theme.spacing(3),
    };
});

export default DraftCard;
